import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, MapPin, Calendar, Sparkle, Users, FileText, Globe, Quotes, TrendUp } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { format } from "date-fns";
import { useAuth } from "@/lib/auth";
import { confImage } from "@/lib/images";

const HERO_IMG = "https://images.unsplash.com/photo-1773829020694-413e879d2957?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb25mZXJlbmNlJTIwaGFsbCUyMGF1ZGllbmNlfGVufDB8fHx8MTc4MTg3MTU1Mnww&ixlib=rb-4.1.0&q=85";

export default function HomePage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [confs, setConfs] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [stats, setStats] = useState({ confs: 0, speakers: 0, papers: 0 });
  const [trending, setTrending] = useState([]);
  const [recs, setRecs] = useState([]);
  const [tow, setTow] = useState(null);
  const [sponsored, setSponsored] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/conferences/all"),
      api.get("/speakers"),
      api.get("/papers"),
      api.get("/meta/trending?limit=10"),
      api.get("/meta/topic-of-week"),
      api.get("/sponsorship/sponsored?limit=4"),
    ]).then(([c, s, p, tr, w, sp]) => {
      setConfs(c.data.slice(0, 4));
      setSpeakers(s.data.slice(0, 6));
      setStats({ confs: c.data.length, speakers: s.data.length, papers: p.data.length });
      setTrending(tr.data || []);
      setTow(w.data && w.data.topic ? w.data : null);
      setSponsored(sp.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user && user.role) {
      api.get("/recommendations?limit=4").then((r) => setRecs(r.data || [])).catch(() => setRecs([]));
    }
  }, [user]);

  const featured = confs[0];
  const year = new Date().getFullYear();

  return (
    <div data-testid="home-page" className="bg-white">
      {/* ===== HERO ===== */}
      <section className="relative border-b border-zinc-200">
        <div className="absolute inset-0 bg-grid opacity-60" />
        <div className="relative px-6 md:px-10 lg:px-16 pt-16 md:pt-24 pb-12 md:pb-20">
          {/* Edition strip */}
          <div className="flex items-center justify-between border-b border-zinc-200 pb-4 mb-12">
            <div className="overline">{t("home.edition", { year })}</div>
            <div className="overline hidden md:block">{t("home.updated")}</div>
            <div className="overline">{t("home.indexing", { confs: stats.confs, speakers: stats.speakers })}</div>
          </div>

          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <motion.h1
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
                className="font-display font-black tracking-[-0.04em] leading-[.86] text-5xl sm:text-6xl md:text-7xl lg:text-[8.5rem] text-zinc-950"
                data-testid="home-hero-title"
              >
                {t("home.heroLine1")}<br />
                {t("home.heroLine2")}<br />
                <span className="italic font-medium" style={{ color: "#2563EB" }}>{t("home.heroLine3")}</span>
              </motion.h1>
              <p className="mt-10 max-w-xl text-zinc-600 text-lg leading-relaxed">
                {t("home.intro")}
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/conferences" data-testid="home-browse-conferences" className="btn-primary">
                  {t("home.browseIndex")} <ArrowUpRight size={16} data-arrow="forward" />
                </Link>
                <Link to="/map" data-testid="home-view-map" className="btn-secondary">
                  <Globe size={16} /> {t("home.openAtlas")}
                </Link>
                <Link to="/speakers/me" data-testid="home-become-speaker" className="btn-accent">
                  <Users size={16} weight="fill" /> {t("home.becomeSpeaker")} <ArrowUpRight size={16} data-arrow="forward" />
                </Link>
              </div>
            </div>

            <div className="md:col-span-4 grid grid-cols-3 md:grid-cols-1 gap-px bg-zinc-200 border border-zinc-200">
              <Stat label={t("home.statConferences")} value={stats.confs} testid="stat-confs" />
              <Stat label={t("home.statSpeakers")} value={stats.speakers} testid="stat-speakers" />
              <Stat label={t("home.statPapers")} value={stats.papers} testid="stat-papers" />
            </div>
          </div>
        </div>
      </section>

      {/* ===== FEATURED BENTO ===== */}
      {featured && (
        <section className="px-6 md:px-10 lg:px-16 pt-20 md:pt-24">
          <div className="flex items-end justify-between border-b border-zinc-950 pb-4 mb-10">
            <div>
              <div className="overline mb-2">{t("home.section01")}</div>
              <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">{t("home.spotlight")}</h2>
            </div>
            <Link to="/conferences" data-testid="home-all-conferences" className="text-sm font-medium link-ul">{t("home.viewFullIndex")}</Link>
          </div>

          <div className="grid md:grid-cols-12 gap-6">
            {/* Big featured */}
            <Link to={`/conferences/${featured.id}`} data-testid="home-featured-conf" className="md:col-span-8 group block">
              <div className="aspect-[16/10] overflow-hidden border border-zinc-200 bg-zinc-100">
                <img src={confImage(featured, { w: 1600, h: 1000 })} alt={featured.title} className="img-mono w-full h-full object-cover" onError={(e) => { e.currentTarget.src = HERO_IMG; }} />
              </div>
              <div className="mt-5 flex items-start gap-6">
                <div className="font-mono text-xs text-zinc-400 pt-1">№ 001</div>
                <div className="flex-1">
                  <div className="flex gap-2 mb-3">
                    <span className="tag-brand">{featured.topic}</span>
                    <span className="tag">{featured.type === "tech" ? t("common.industry") : t("common.academic")}</span>
                  </div>
                  <h3 className="font-display font-bold text-3xl md:text-4xl tracking-tight leading-[1.05] group-hover:text-blue-700 transition-colors">
                    {featured.title}
                  </h3>
                  <p className="mt-3 text-zinc-600 line-clamp-2">{featured.description}</p>
                  <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-sm text-zinc-700 font-mono">
                    <span className="flex items-center gap-1.5"><Calendar size={14} /> {format(new Date(featured.start_date), "MMM d, yyyy")}</span>
                    <span className="flex items-center gap-1.5"><MapPin size={14} /> {featured.city}, {featured.country}</span>
                  </div>
                </div>
              </div>
            </Link>

            {/* Sidebar mini list */}
            <div className="md:col-span-4 border border-zinc-200 bg-white divide-y divide-zinc-200">
              {confs.slice(1, 4).map((c, idx) => (
                <Link key={c.id} to={`/conferences/${c.id}`} data-testid={`home-conf-card-${idx}`} className="block p-5 hover:bg-[#FAFAFA] group transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-xs text-zinc-400">№ {String(idx + 2).padStart(3, "0")}</span>
                    <span className="tag">{c.type === "tech" ? t("common.industry") : t("common.academic")}</span>
                  </div>
                  <h4 className="font-display font-semibold text-xl tracking-tight leading-tight group-hover:text-blue-700 transition-colors">
                    {c.title}
                  </h4>
                  <div className="mt-2 text-xs text-zinc-500 font-mono flex items-center gap-3">
                    <span>{format(new Date(c.start_date), "MMM d")}</span>
                    <span>·</span>
                    <span>{c.city}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== SPONSORED ===== */}
      {sponsored.length > 0 && (
        <section className="px-6 md:px-10 lg:px-16 pt-20 md:pt-24" data-testid="sponsored-section">
          <div className="flex items-end justify-between border-b border-zinc-200 pb-3 mb-6">
            <div>
              <div className="overline mb-1 flex items-center gap-1.5 text-blue-700">✦ {t("home.sponsored")}</div>
              <div className="text-xs font-mono text-zinc-400">{t("home.sponsoredDisclosure")}</div>
            </div>
            <Link to="/sponsor" data-testid="home-promote-link" className="text-xs link-ul font-medium">{t("home.promoteYours")} →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {sponsored.map((c, i) => (
              <Link key={c.id} to={`/conferences/${c.id}`} data-testid={`sponsored-card-${i}`} className="block card overflow-hidden group">
                <div className="aspect-[16/9] bg-zinc-100 overflow-hidden border-b border-zinc-200 relative">
                  <img src={confImage(c)} alt={c.title} loading="lazy" className="img-mono w-full h-full object-cover" />
                  <span className="absolute top-2 left-2 rtl:left-auto rtl:right-2 bg-zinc-950 text-white text-[10px] font-mono uppercase tracking-wider px-2 py-1">✦ {t("home.sponsored")}</span>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2"><span className="tag">{c.topic}</span></div>
                  <h3 className="font-display font-bold text-base tracking-tight leading-tight group-hover:text-blue-700 transition-colors line-clamp-2">{c.title}</h3>
                  <div className="mt-2 text-xs text-zinc-500 font-mono">{format(new Date(c.start_date), "MMM d")} · {c.city}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ===== TOPIC OF THE WEEK ===== */}
      {tow && tow.conferences && tow.conferences.length > 0 && (
        <section className="px-6 md:px-10 lg:px-16 pt-20 md:pt-24" data-testid="topic-of-week">
          <div className="border-2 border-blue-600 bg-blue-50 p-8 md:p-12">
            <div className="grid md:grid-cols-12 gap-8 items-end">
              <div className="md:col-span-7">
                <div className="overline mb-3" style={{color:'#1D4ED8'}}>
                  {t("home.weekFocus", { week: String(tow.week).padStart(2, "0"), year: tow.year })}
                </div>
                <h2 className="font-display font-black text-4xl md:text-6xl tracking-[-0.03em] leading-[.95] text-zinc-950">
                  {tow.topic}
                </h2>
                <p className="mt-4 text-zinc-700 text-base max-w-md leading-relaxed">
                  {t("home.topicInfo", { n: tow.count, topic: tow.topic })}
                </p>
              </div>
              <div className="md:col-span-5">
                <Link to={`/conferences?topic=${encodeURIComponent(tow.topic)}`} data-testid="tow-explore" className="btn-accent">
                  {t("home.exploreAll")} <ArrowUpRight size={14} data-arrow="forward" />
                </Link>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {tow.conferences.map((c, i) => (
                <Link key={c.id} to={`/conferences/${c.id}`} data-testid={`tow-card-${i}`} className="bg-white border border-zinc-200 hover:border-zinc-950 p-5 transition-all hover:translate-y-[-2px]">
                  <div className="font-mono text-[10px] text-zinc-400 mb-2">№ {String(i+1).padStart(2,"0")}</div>
                  <div className="font-display font-bold text-lg tracking-tight leading-tight group-hover:text-blue-700 line-clamp-2">{c.title}</div>
                  <div className="mt-2 text-xs text-zinc-500 font-mono">
                    {format(new Date(c.start_date), "MMM d")} · {c.city}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== FEATURES ===== */}
      <section className="mt-20 md:mt-28 border-t border-b border-zinc-200 bg-[#FAFAFA]">
        <div className="px-6 md:px-10 lg:px-16 py-16 md:py-20">
          <div className="grid md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-4">
              <div className="overline mb-3">{t("home.whySymposia")}</div>
              <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tight leading-[1.02]">
                {t("home.builtFor")}
              </h2>
            </div>
            <div className="md:col-span-8 grid sm:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
              {[
                { i: <Sparkle size={22} weight="duotone" />, t: t("home.feat1Title"), d: t("home.feat1Body") },
                { i: <Users size={22} weight="duotone" />, t: t("home.feat2Title"), d: t("home.feat2Body") },
                { i: <FileText size={22} weight="duotone" />, t: t("home.feat3Title"), d: t("home.feat3Body") },
              ].map((f) => (
                <div key={f.t} className="bg-[#FAFAFA] p-7">
                  <div className="text-blue-600 mb-4">{f.i}</div>
                  <h3 className="font-display font-semibold text-lg mb-1.5 tracking-tight">{f.t}</h3>
                  <p className="text-sm text-zinc-600 leading-relaxed">{f.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== SPEAKERS ===== */}
      <section className="px-6 md:px-10 lg:px-16 py-20 md:py-24">
        <div className="flex items-end justify-between border-b border-zinc-950 pb-4 mb-10">
          <div>
            <div className="overline mb-2">{t("home.section02")}</div>
            <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">{t("home.featuredSpeakers")}</h2>
          </div>
          <Link to="/speakers" data-testid="home-all-speakers" className="text-sm font-medium link-ul">{t("home.allSpeakers")}</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
          {speakers.map((s, idx) => (
            <Link key={s.id} to={`/speakers/${s.id}`} data-testid={`home-speaker-${idx}`} className="block group">
              <div className="aspect-[3/4] bg-zinc-100 border border-zinc-200 overflow-hidden">
                {s.image_url ? (
                  <img src={s.image_url} alt={s.name} className="img-mono w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-display text-5xl font-black text-zinc-300">{s.name?.[0]}</div>
                )}
              </div>
              <div className="mt-3">
                <div className="font-display font-semibold text-base tracking-tight leading-tight group-hover:text-blue-700 transition-colors">{s.name}</div>
                <div className="text-xs text-zinc-500 font-mono mt-1">{s.organization}</div>
              </div>
            </Link>
          ))}
        </div>

        {/* Speaker CTA — visible to everyone, drives self-service onboarding */}
        <div className="mt-12 border-t border-zinc-200 dark:border-zinc-800 pt-10 flex flex-col md:flex-row items-start md:items-end md:justify-between gap-6">
          <div className="max-w-xl">
            <div className="overline mb-2">Public Directory</div>
            <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight leading-tight">
              Are you a speaker or researcher?
            </h3>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-2 leading-relaxed">
              Publish your profile in the Symposia index — surface your bio, expertise,
              past talks, and booking links to a global audience of conference organizers.
              Free to publish.
            </p>
          </div>
          <Link
            to="/speakers/me"
            data-testid="home-become-speaker-secondary"
            className="btn-primary inline-flex items-center gap-2 whitespace-nowrap"
          >
            Become a Speaker
            <ArrowUpRight size={16} weight="bold" />
          </Link>
        </div>
      </section>

      {/* ===== RECOMMENDATIONS (signed-in only) ===== */}
      {user && user.role && recs.length > 0 && (
        <section className="px-6 md:px-10 lg:px-16 py-20 md:py-24 border-t border-zinc-200">
          <div className="flex items-end justify-between border-b border-zinc-950 pb-4 mb-10">
            <div>
              <div className="overline mb-2">{t("home.forYou")}</div>
              <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">{t("home.youMightLike")}</h2>
            </div>
            <div className="text-xs font-mono text-zinc-500">{t("home.basedOnBookmarks")}</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {recs.map((c, i) => (
              <Link key={c.id} to={`/conferences/${c.id}`} data-testid={`home-rec-${i}`} className="block card overflow-hidden group">
                <div className="aspect-[16/9] bg-zinc-100 overflow-hidden border-b border-zinc-200">
                  <img src={confImage(c)} alt={c.title} loading="lazy" className="img-mono w-full h-full object-cover" />
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2"><span className="tag">{c.topic}</span></div>
                  <h3 className="font-display font-bold text-base tracking-tight leading-tight group-hover:text-blue-700 transition-colors line-clamp-2">{c.title}</h3>
                  <div className="mt-2 text-xs text-zinc-500 font-mono">{format(new Date(c.start_date), "MMM d")} · {c.city}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ===== TRENDING TOPICS ===== */}
      {trending.length > 0 && (
        <section className="px-6 md:px-10 lg:px-16 py-20 md:py-24 border-t border-zinc-200">
          <div className="flex items-end justify-between border-b border-zinc-950 pb-4 mb-10">
            <div>
              <div className="overline mb-2 flex items-center gap-2"><TrendUp size={12} /> {t("home.trending")}</div>
              <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">{t("home.topicsInMotion")}</h2>
            </div>
            <div className="text-xs font-mono text-zinc-500">{t("home.confsIndexed", { n: trending.reduce((a,b)=>a+b.count,0) })}</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-zinc-200 border border-zinc-200">
            {trending.map((tp, i) => (
              <Link key={tp.topic} to={`/conferences?topic=${encodeURIComponent(tp.topic)}`} data-testid={`trending-${i}`} className="bg-white p-5 hover:bg-blue-50 transition-colors group">
                <div className="font-mono text-[10px] text-zinc-400">№ {String(i+1).padStart(2,"0")}</div>
                <div className="font-display font-semibold text-base mt-1 group-hover:text-blue-700 leading-tight">{tp.topic}</div>
                <div className="font-display font-black text-3xl mt-2 tabular">{tp.count}</div>
                <div className="overline mt-1">{t("home.statConferences").toLowerCase()}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ===== Pull Quote ===== */}
      <section className="px-6 md:px-10 lg:px-16 py-20 md:py-24 border-t border-zinc-200">
        <div className="max-w-4xl mx-auto text-center">
          <Quotes size={36} className="mx-auto text-blue-600 mb-6" weight="fill" />
          <p className="font-display font-medium text-3xl md:text-5xl tracking-tight leading-tight text-zinc-950">
            "{t("home.pullQuote")}"
          </p>
          <div className="mt-8 overline">{t("home.editorsByline")}</div>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white p-5 md:p-6" data-testid={testid}>
      <div className="font-display font-bold text-4xl md:text-5xl tracking-tight tabular leading-none">{value}</div>
      <div className="overline mt-2">{label}</div>
    </div>
  );
}
